import React, {useState, useEffect, useCallback, useContext} from 'react';
import {Link} from 'gatsby';

import CustomInput from '../../custom-input';
import Info from './info';
import Result from './qr-generator֊result';
import {toolRequest, request} from '../../../requests';
import ProgressWithHint from '../../progress-hint';
import GoToLoginPage from '../tools-login';
import LimitReached from '../limit-modal';
import {returnSocketLink, freeToolsErrors, ERROR_MESSAGES} from '../../../helpers/constants';
import {UserContext, UserType} from '../../../helpers/contexts/user-context';

import './styles.scss';
import {useQuery} from '@apollo/react-hooks';
import {GET_HEXOMATIC_USER} from '../../../graphql/queries';
import {HexomaticUserType} from '../../../helpers/contexts/hexomatic-user';
import {getHintPopupPerTool} from '../../../helpers';

const QrGeneratorWrapper = () => {
  const hintName = 'free-tools-hint-qr';
  const {user} = useContext(UserContext);

  const [value, setValue] = useState('');
  const [socket, setSocket] = useState<any>(null);
  const [socketId, setSocketId] = useState('');
  const [result, setResult] = useState([]);
  const [error, setError] = useState('');
  const [limit, setLimit] = useState('');
  const [loading, setLoading] = useState(false);
  const [limitReached, setLimitReached] = useState(false);
  const [show, setShow] = useState(false);
  const [showCreditsModal, setShowCreditsModal] = useState(false);
  const [clientId, setClientId] = useState('');
  const [mainUser, setUser] = useState<null | UserType>(null);
  const [hexomaticUser, setHexomaticUser] = useState<null | HexomaticUserType>(null);

  const {data: hexomaticUserData} = useQuery(GET_HEXOMATIC_USER, {
    fetchPolicy: 'no-cache',
    skip: !!getHintPopupPerTool(hintName),
  });
  //----------useEffect-------------------
  useEffect(() => {
    if (hexomaticUserData && hexomaticUserData.HexomaticUser && hexomaticUserData.HexomaticUser.getByLegacyId) {
      setHexomaticUser(hexomaticUserData.HexomaticUser.getByLegacyId);
    }
    if (hexomaticUserData && hexomaticUserData.User && hexomaticUserData.User.get) {
      setUser(hexomaticUserData.User.get);
    }
  }, [hexomaticUserData]);

  useEffect(() => {
    socketStart();
  }, []);
  useEffect(() => {
    if (!socket) return;
    socket.onerror = function() {
      console.log('Connection Error');
    };

    socket.onopen = function() {
      console.log('WebSocket Client Connected');
    };
    socket.onclose = () => {
      socketStart();
    };
    return () => {
      socket.close();
    };
  }, [socket]);

  useEffect(() => {
    if (!socket) return;
    socket.onmessage = function(e: any) {
      const data = e ? JSON.parse(e.data) : '';
      if (data && data.clientId !== clientId) {
        setClientId(data.clientId);
        return;
      }
      if (data && freeToolsErrors.includes(data)) {
        setLimit(data);
        setShow(false);
        setLoading(false);
        setSocketId('');
        return;
      }
      if (data && data.socketId !== socketId) return;
      setResult(data);
      setShow(false);
      setSocketId('');
    };
  }, [socket, socketId, clientId]);
  //--------------------Functions---------------------------
  const socketStart = () => {
    //@ts-ignore
    const newSocket = new WebSocket(returnSocketLink());
    setSocket(newSocket);
  };
  const handleClick = useCallback(async () => {
    if (!value || !value.trim()) {
      return;
    }
    // if (!validURL(value.trim())) {
    //   setError('URL is not valid');
    //   return;
    // }
    setError('');
    setShow(false);
    if (!getHintPopupPerTool(hintName) && user && user.id) {
      setShowCreditsModal(true);
      return;
    }
    try {
      setLoading(true);

      const res = await toolRequest('qr-code', value.trim(), {
        clientId,
        requestSource: 'free_tools',
      });
      if (res.status === 403) {
        setLimitReached(true);
        return;
      }
      if (res?.data?.socketId) {
        setSocketId(res.data.socketId);
        setResult([]);
        setShow(true);
      } else if (res.errorData) {
        if (ERROR_MESSAGES[res.errorData.message]) {
          setLimit(ERROR_MESSAGES[res.errorData.message]);
        } else {
          setError('Something went wrong. Please try again.');
        }
      }
      setLoading(false);
    } catch (error) {
      setError('Something went wrong. Please try again.');
      setLoading(false);
    }
  }, [value, setError]);

  const handleChangeModalState = () => {
    setLimitReached(false);
  };

  const getResult = async () => {
    if (!socketId) return;
    try {
      const res: any = await request(`result/${socketId}`);
      if (
        res &&
        res.data &&
        res.data.result &&
        typeof res.data.result === 'string' &&
        freeToolsErrors.includes(res.data.result)
      ) {
        setLimit(res.data.result);
        setShow(false);
        setSocketId('');
        return;
      }
      if (res && res.data && res.data.result && res.data.result._tech_stack) {
        setResult(res.data.result._tech_stack.length > 0 ? res.data.result._tech_stack : ['Not found']);
        setShow(false);
      }
    } catch (e) {
      console.log(e);
    }
  };

  return (
    <div className="qr-main-wrapper">
      <div className="d-flex pb-2" style={{maxWidth: '752px', margin: 'auto'}}>
        <Link className="n-wrap" to="/tools">
          ← Back to tools
        </Link>
        <Link to="/tools/history/#qr-code-generator" className="n-wrap ml-auto">
          See history →
        </Link>
      </div>
      <CustomInput
        onClick={handleClick}
        onChange={setValue}
        value={value}
        placeholder="Type any URL, number or text"
        btnName="Generate"
        error={error}
        disabled={(loading && result.length < 1) || show}
        maxLength={2000}
      />
      {Object.keys(result).length ? <Result data={result} scanUrl={value} /> : null}
      <div className={`mx-auto col-12 my-5 ${socketId && result.length < 1 ? 'visible' : 'invisible'}`}>
        {show && <ProgressWithHint func={getResult} />}
      </div>
      <Info />
      {limitReached && <GoToLoginPage toolName="QR generator" setOpen={handleChangeModalState} />}
      {limit && <LimitReached text={limit} setLimit={setLimit} />}
      {showCreditsModal && (
        <LimitReached
          hint_name={hintName}
          defaultModal
          hideModal={setShowCreditsModal}
          automation_credits={1}
          confirm={handleClick}
          user={mainUser}
          hexomaticUser={hexomaticUser}
        />
      )}
    </div>
  );
};

export default QrGeneratorWrapper;
